import { useId, useState } from 'react'
import { formatCompact, formatValue } from './palette'
import { useT } from '@/i18n'
import { uiMessages } from '@/i18n/messages/ui'

export interface Bar {
  label: string
  value: number
  /** Shown in the tooltip and the table view beside the value. */
  note?: string
}

interface Props {
  title: string
  subtitle?: string
  bars: Bar[]
  unit: string
  color: string
  /** 'column' = vertical, for time. 'bar' = horizontal, for ranked categories. */
  orientation?: 'column' | 'bar'
}

/**
 * A single-series bar/column chart in plain SVG.
 *
 * Hand-rolled rather than pulled from a chart library because the mark specs are the
 * point: ≤24px thick, 4px rounded data-end square at the baseline, a 2px surface gap
 * between neighbours, hairline recessive gridlines. Those are easier to guarantee
 * here than to coax out of a library's theme.
 *
 * No legend: a single series needs none — the title says what is plotted, and a box
 * with one swatch would just restate it.
 */
export function BarChart({ title, subtitle, bars, unit, color, orientation = 'column' }: Props) {
  const t = useT(uiMessages)
  const [hover, setHover] = useState<number | null>(null)
  const [showTable, setShowTable] = useState(false)
  const tableId = useId()

  const max = Math.max(...bars.map((b) => b.value), 0) || 1
  const horizontal = orientation === 'bar'

  return (
    <figure className="rounded-2xl bg-white p-3 ring-1 ring-neutral-200 sm:p-5 dark:bg-neutral-900 dark:ring-neutral-800">
      <figcaption className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{title}</h3>
          {subtitle && <p className="mt-0.5 text-xs text-neutral-500">{subtitle}</p>}
        </div>
        {/* The table view is the relief for aqua's sub-3:1 contrast in light mode,
            and the accessible fallback generally. Never remove it. */}
        <button
          onClick={() => setShowTable((v) => !v)}
          aria-expanded={showTable}
          aria-controls={tableId}
          className="shrink-0 rounded-md px-2 py-1 text-xs text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
        >
          {showTable ? t('viewChart') : t('viewTable')}
        </button>
      </figcaption>

      {showTable ? (
        <table id={tableId} className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 text-left text-xs text-neutral-500 dark:border-neutral-800">
              <th className="pb-2 font-medium">{t('colLabel')}</th>
              <th className="pb-2 text-right font-medium">{t('colValue')}</th>
            </tr>
          </thead>
          <tbody>
            {bars.map((b) => (
              <tr key={b.label} className="border-b border-neutral-100 last:border-0 dark:border-neutral-800/60">
                <td className="py-1.5 text-neutral-700 dark:text-neutral-300">
                  {b.label}
                  {b.note && <span className="ml-2 text-xs text-neutral-500">{b.note}</span>}
                </td>
                <td className="tabular py-1.5 text-right text-neutral-900 dark:text-neutral-100">
                  {formatValue(b.value, unit)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : horizontal ? (
        <div className="space-y-1.5">
          {bars.map((b, i) => (
            <div
              key={b.label}
              className="group grid grid-cols-[minmax(0,11rem)_1fr_auto] items-center gap-3"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            >
              <span className="truncate text-xs text-neutral-600 dark:text-neutral-400" title={b.label}>
                {b.label}
              </span>
              <div className="h-5 rounded-r-[4px]">
                <div
                  className="h-full rounded-r-[4px] transition-[width]"
                  style={{
                    width: `${Math.max((b.value / max) * 100, 0.6)}%`,
                    backgroundColor: color,
                    opacity: hover === null || hover === i ? 1 : 0.45,
                  }}
                />
              </div>
              {/* Value at the tip — a direct label, and the relief the validator asks for. */}
              <span className="tabular shrink-0 text-xs text-neutral-700 dark:text-neutral-300">
                {formatCompact(b.value, unit)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex h-44 w-full items-end gap-[2px] overflow-hidden">
          {bars.map((b, i) => (
            <div
              key={b.label}
              className="group flex h-full min-w-0 flex-1 flex-col justify-end"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              title={`${b.label}: ${formatValue(b.value, unit)}${b.note ? ` (${b.note})` : ''}`}
            >
              <span
                className={`tabular mb-1 text-center text-[10px] text-neutral-600 transition-opacity dark:text-neutral-400 ${
                  hover === i ? 'opacity-100' : 'opacity-0'
                }`}
              >
                {formatCompact(b.value, unit)}
              </span>
              <div
                // 4px rounded data-end, square at the baseline; ≤24px thick via max-width.
                className="mx-auto w-full max-w-[24px] rounded-t-[4px] transition-[height,opacity]"
                style={{
                  height: `${Math.max((b.value / max) * 100, 0.8)}%`,
                  backgroundColor: color,
                  opacity: hover === null || hover === i ? 1 : 0.45,
                }}
              />
              <span className="mt-1.5 truncate text-center text-[10px] text-neutral-500">{b.label}</span>
            </div>
          ))}
        </div>
      )}
    </figure>
  )
}
